import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle2, Eye, Layers, Ruler, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';

const Plans3D = () => {
  const navigate = useNavigate();

  const advantages = [
    {
      icon: Eye,
      title: 'Visualisation réaliste',
      description: 'Voyez votre future salle de bains avant le début des travaux'
    },
    {
      icon: Layers,
      title: 'Plusieurs options',
      description: 'Comparez différentes configurations et styles'
    },
    {
      icon: Ruler,
      title: 'À l\'échelle',
      description: 'Plans précis avec dimensions et implantations exactes'
    }
  ];

  const process = [
    'Prise de mesures précises de votre salle de bains',
    'Discussion sur vos goûts, besoins et budget',
    'Création de 1 à 3 propositions en 3D',
    'Présentation des rendus photo-réalistes',
    'Ajustements selon vos retours',
    'Validation du projet final avant chiffrage détaillé'
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[500px] bg-gradient-to-br from-[#336DBF] to-[#2B2B2B]">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='180' height='180' filter='url(%23n)' opacity='.18'/%3E%3C/svg%3E")`,
        }} />
        
        <div className="relative h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl"
            >
              <h1 
                className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                Plans 3D & Projections Photo-réalistes
              </h1>
              <p className="text-xl text-slate-200 mb-8">
                Visualisez votre future salle de bains avant de commencer les travaux.
                Un outil de décision pour valider les grandes orientations de votre projet.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-8 bg-[#336DBF]/10 border-y border-[#336DBF]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-[#336DBF] flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-lg text-[#2B2B2B] mb-2">
                Service complémentaire à la demande
              </h3>
              <p className="text-slate-700">
                Les plans 3D <strong>ne sont pas inclus automatiquement</strong> dans le devis standard.
                Ce service est particulièrement pertinent pour les gros travaux, les modifications importantes,
                ou lorsque plusieurs options d'aménagement doivent être comparées.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Purpose */}
      <section className="py-14 sm:py-18 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="text-3xl font-bold text-[#2B2B2B] mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Pourquoi des plans 3D?
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Les plans 3D permettent de <strong>valider les grandes orientations esthétiques et fonctionnelles</strong> de votre projet
              avant de discuter précisément du prix final. Ce n'est pas juste un rendu visuel, c'est un{' '}
              <strong>outil pour cadrer le projet et éviter les erreurs</strong> avant le chiffrage détaillé.
            </p>
            <p className="text-lg text-slate-700 leading-relaxed">
              Vous pourrez voir concrètement à quoi ressemblera votre salle de bains, tester différentes configurations,
              et vous assurer que le résultat correspond parfaitement à vos attentes avant de vous engager.
            </p>
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-14 sm:py-18 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#2B2B2B] mb-12 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Les avantages
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {advantages.map((advantage, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="h-full border-slate-200/70 hover:shadow-lg transition-shadow duration-200">
                  <CardContent className="p-8 space-y-4">
                    <div className="w-14 h-14 rounded-lg bg-[#336DBF]/10 flex items-center justify-center">
                      <advantage.icon className="w-7 h-7 text-[#336DBF]" />
                    </div>
                    <h3 className="text-xl font-bold text-[#2B2B2B]">{advantage.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{advantage.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-14 sm:py-18 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#2B2B2B] mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Comment ça marche?
              </h2>
              <ul className="space-y-4">
                {process.map((step, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#336DBF] text-white flex items-center justify-center flex-shrink-0 font-semibold text-sm">
                      {i + 1}
                    </div>
                    <span className="text-slate-700 pt-1">{step}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <Card className="border-slate-200/70 shadow-xl">
                <CardContent className="p-8 space-y-6">
                  <h3 className="text-xl font-bold text-[#2B2B2B]">Information importante</h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#336DBF] flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-slate-700">
                        <strong>1 à 3 propositions</strong> selon la complexité du projet
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#336DBF] flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-slate-700">
                        <strong>Rendus photo-réalistes</strong> pour une visualisation claire
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#336DBF] flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-slate-700">
                        <strong>Ajustements inclus</strong> pour affiner le projet
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#336DBF] flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-slate-700">
                        <strong>Délai</strong> : 5-7 jours après la prise de mesures
                      </p>
                    </div>
                  </div>
                  <div className="pt-4 border-t border-slate-200">
                    <p className="text-sm text-slate-600">
                      <strong>Tarif</strong> : Sur devis selon la complexité du projet.
                      Forfait généralement entre 500 et 1’500 CHF.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 sm:py-18 lg:py-24 bg-[#2B2B2B] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Besoin de visualiser votre projet?
          </h2>
          <p className="text-xl text-slate-300">
            Demandez un devis incluant les plans 3D pour sécuriser votre investissement.
          </p>
          <Button 
            onClick={() => navigate('/devis')}
            size="lg"
            className="bg-[#336DBF] hover:bg-[#2F62AA] text-white font-medium px-8 py-6 text-base"
          >
            Demander un devis avec plans 3D
          </Button>
          <p className="text-sm text-slate-400 pt-4">
            Mentionnez votre intérêt pour les plans 3D dans le formulaire de devis
          </p>
        </div>
      </section>
    </div>
  );
};

export default Plans3D;